import authApi from "@/api/auth";
import authStore from "@/store/auth";
import { useInvitations } from "@/composables/useInvitations";
import { resetDynamicRoutes } from "@/router/routeManager";
import { clearCache as clearContentCache } from "@/components/mailboxes/contentCache";
import { clearAll as clearResultTabsCache } from "@/components/indexSources/useResultTabs";

class AuthManager {
  constructor() {
    this.isProcessing = false;
    this.refreshFailed = false;
    this.globalQueue = [];
    this.channel = new BroadcastChannel("token-sync");
  }

  initialize() {
    this.channel.onmessage = ({ data }) => {
      if (data.type === "logout") {
        this.logout();
      }
    };
  }

  async wait() {
    if (this.isProcessing) {
      return new Promise((resolve, reject) => this.globalQueue.push({ resolve, reject })).catch(() => {});
    }
  }

  async refreshToken() {
    if (this.isProcessing) {
      return this.wait();
    }

    this.isProcessing = true;
    try {
      this.refreshFailed = false;
      const response = await authApi.refresh();
      if (response.error) {
        throw new Error(response.error.message);
      }
      authStore.setIsAuthenticated(true);
      this.processGlobalQueue();
    } catch (error) {
      this.refreshFailed = true;
      this.processGlobalQueue(error);
      this.logout();
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  async validateSession() {
    if (this.isProcessing) {
      return this.wait();
    }

    this.isProcessing = true;
    try {
      const response = await authApi.validateSession();
      if (response.error) {
        authStore.setIsAuthenticated(false);
        this.logout();
        this.processGlobalQueue(new Error(response.error.message));
        return false;
      }
      const { data } = response;
      const valid = !!data.valid;
      if (valid) {
        authStore.setIsAuthenticated(valid);
      } else {
        this.logout();
      }
      this.processGlobalQueue(null, valid);
      return valid;
    } catch (error) {
      this.processGlobalQueue(error);
      throw error;
    } finally {
      this.isProcessing = false;
    }
  }

  processGlobalQueue(error = null, result = null) {
    this.globalQueue.forEach(({ resolve, reject }) => {
      error ? reject(error) : resolve(result);
    });
    this.globalQueue = [];
  }

  async logout() {
    authStore.clear();
    useInvitations().reset();
    resetDynamicRoutes();
    // Clear per-user IndexedDB caches before the reload — IndexedDB survives navigation/reload,
    // unlike in-memory Pinia stores and module-level refs, which the full reload below wipes.
    await Promise.allSettled([clearContentCache(), clearResultTabsCache()]);
    // Full page reload (not router.replace) so every in-memory store/ref for the
    // previous user is discarded, not just the current route's state.
    window.location.href = "/login";
  }

  userLogout() {
    this.channel.postMessage({ type: "logout" });
    authApi.logout();
    this.logout();
  }

  cleanup() {
    this.channel.close();
  }
}

export const authManager = new AuthManager();
